import type { ErrorEvent, EventHint } from "@sentry/react";
import * as Sentry from "@sentry/react";

import { isQuotaExceededError, localStorageCheck } from "@/utils/localStorage";

export type SentryConfig = {
  environment: string;
  dsn: string;
  mode: string;
  release?: string;
};

export function initializeSentry(config: SentryConfig): void {
  Sentry.init({
    enabled: config.environment !== "local",
    dsn: config.dsn,
    environment: config.environment ?? config.mode,
    release: config.release,
    integrations: [
      Sentry.browserTracingIntegration({
        idleTimeout: 5000,
        finalTimeout: 60000,
        childSpanTimeout: 15000,
        shouldCreateSpanForRequest: (url) => {
          return !/honeycomb\.io|launchdarkly\.com|sentry\.io|amplitude\.com/.test(url);
        },
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    ignoreErrors: [
      "Failed to connect to server after exhausting retry policy.",
      "Failed to update a ServiceWorker for scope",
      "b.match is not a function",
      "gtm|gtag",
      // Transient PGlite portal/cursor collision on the single-connection WASM Postgres.
      // Already retried at the client layer (`installPortalRetry`); this is a safety net
      // for unhandled DrizzleQueryError-wrapped rejections that bypass `executeQuery`
      // (e.g. live-query refreshes on `fetchChannelList` — WEB-2006, SVS-2397..2406).
      "cannot drop active portal",
      // Benign HTMLMediaElement race: pause()/load() called before play() promise resolves.
      "The play() request was interrupted",
    ],
    enableLogs: true,

    beforeSend(event, hint) {
      if (isQuotaExceededError(hint.originalException)) {
        localStorageCheck();
        event.level = "warning";
        event.fingerprint = ["quota-exceeded-error"];
      }
      // WEB-1997: PGlite 0.4.x eagerly prefetches pglite.wasm / initdb.wasm at
      // module-eval time. In Safari Private Browsing the prefetch fetch()
      // rejects with `TypeError: Load failed` and the rejection is unhandled
      // because PGlite never attaches a .catch to the cached promise. The user
      // already sees the StorageError UI via the storageInitError short-circuit
      // in db.ts, so this rejection is purely upstream noise. Drop it.
      if (isPgliteWasmPrefetchRejection(event, hint)) {
        return null;
      }
      return event;
    },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const PGLITE_FRAME_PATTERN = /@electric-sql\/pglite|pglite\.wasm|initdb\.wasm/;

/**
 * Detects unhandled rejections originating from PGlite's eager wasm prefetch.
 *
 * PGlite 0.4.x schedules `fetch()` calls for `pglite.wasm` and `initdb.wasm` at module-eval time and stores the
 * in-flight promise in a cache map without attaching a `.catch`. In Safari Private Browsing those fetches reject with
 * the WebKit-generic `TypeError: Load failed`, which then escapes as an unhandled promise rejection (mechanism
 * `onunhandledrejection`).
 *
 * The user-visible UX is already handled by the `storageInitError` / `StorageError` path; capturing this rejection in
 * Sentry would only be noise. Match precisely on exception type/value, the `onunhandledrejection` mechanism, and at
 * least one stack frame inside the @electric-sql/pglite bundle so unrelated `Load failed` rejections still report.
 */
export function isPgliteWasmPrefetchRejection(event: ErrorEvent, hint: EventHint): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  if (exception.type !== "TypeError" || exception.value !== "Load failed") return false;

  const mechanism = exception.mechanism?.type;
  if (mechanism && mechanism !== "onunhandledrejection") return false;

  const frames = exception.stacktrace?.frames ?? [];
  const hasPgliteFrame = frames.some(
    (frame) => PGLITE_FRAME_PATTERN.test(frame.filename ?? "") || PGLITE_FRAME_PATTERN.test(frame.module ?? ""),
  );
  if (hasPgliteFrame) return true;

  // Sentry sometimes strips the synthetic stack from cross-origin/CORS-tainted unhandled rejections, leaving only the
  // original error reference. Fall back to the raw error's stack.
  const original = hint.originalException;
  const rawStack = original instanceof Error ? (original.stack ?? "") : "";
  return PGLITE_FRAME_PATTERN.test(rawStack);
}
